import type {
  AccountBranch,
  AccountContact,
  AccountStatus,
  CatalogList,
  ContactStatus,
  CustomerAccountDetail,
  CustomerAccountSummary,
  OutOfTermPolicy,
  PortalSignupMode,
} from '@central/contracts';
import { api, get, post } from '@/lib/api';

/**
 * Acesso à administração de clientes (`/accounts/*`).
 *
 * Uma função por rota do servidor, sem nenhuma inventada, e os tipos vindos de
 * `@central/contracts`: a API e a tela leem a mesma declaração, e um campo que
 * mude de forma quebra a compilação em vez de virar `undefined` na tela.
 *
 * Note o que **não** existe aqui: `apagarCliente` e `apagarContato`. Não é
 * esquecimento — o servidor não tem `DELETE` nessas rotas. Um cliente carrega
 * chamados e histórico, e um contato é o solicitante de tudo o que ele abriu:
 * o desligamento dos dois é `status`, e o servidor derruba as sessões como
 * consequência do estado resultante.
 */

const patch = <T>(caminho: string, corpo: unknown) =>
  api<T>(caminho, { method: 'PATCH', body: corpo });

// ── clientes ───────────────────────────────────────────────────────────────

export interface FiltroDeClientes {
  q?: string;
  status?: AccountStatus;
}

export interface NovoCliente {
  code: string;
  /** Ausente quando se promove uma organização já cadastrada. */
  legalName?: string;
  tradeName?: string;
  taxId?: string;
  domain?: string;
  /** Promove uma organização existente a cliente, em vez de criar outra. */
  organizationId?: string;
  status?: AccountStatus;
}

export interface AlteracaoDeCliente {
  code?: string;
  legalName?: string;
  tradeName?: string;
  taxId?: string;
  domain?: string;
  status?: AccountStatus;
  portalEnabled?: boolean;
  portalEmailDomains?: string[];
  portalSelfSignup?: PortalSignupMode;
  outOfTermPolicy?: OutOfTermPolicy;
  outOfTermGraceDays?: number;
  notes?: string;
  version?: number;
}

export function listarClientes(filtro: FiltroDeClientes = {}) {
  const busca = new URLSearchParams();
  if (filtro.q) busca.set('q', filtro.q);
  if (filtro.status) busca.set('status', filtro.status);
  const consulta = busca.toString();
  return get<CatalogList<CustomerAccountSummary>>(`/accounts${consulta ? `?${consulta}` : ''}`);
}

export const obterCliente = (id: string) => get<CustomerAccountDetail>(`/accounts/${id}`);

export const criarCliente = (corpo: NovoCliente) =>
  post<CustomerAccountDetail>('/accounts', corpo);

export const alterarCliente = (id: string, corpo: AlteracaoDeCliente) =>
  patch<CustomerAccountDetail>(`/accounts/${id}`, corpo);

export const listarFiliais = (id: string) =>
  get<CatalogList<AccountBranch>>(`/accounts/${id}/branches`);

// ── contatos do cliente ────────────────────────────────────────────────────

export interface NovoContato {
  email: string;
  name: string;
  jobTitle?: string;
  phone?: string;
  status?: 'INVITED' | 'ACTIVE';
  initialPassword?: string;
}

/**
 * O e-mail **não** entra na alteração, e o tipo diz isso.
 *
 * O servidor responde 400 a `PATCH { email }`: o endereço é a identidade de
 * login e a chave por que a resposta do cliente cai no chamado certo. O erro
 * aparece na compilação em vez de na tela.
 */
export interface AlteracaoDeContato {
  name?: string;
  jobTitle?: string;
  phone?: string;
  status?: ContactStatus;
  version?: number;
}

export const listarContatos = (accountId: string) =>
  get<CatalogList<AccountContact>>(`/accounts/${accountId}/contacts`);

export const criarContato = (accountId: string, corpo: NovoContato) =>
  post<AccountContact>(`/accounts/${accountId}/contacts`, corpo);

export const alterarContato = (accountId: string, contactId: string, corpo: AlteracaoDeContato) =>
  patch<AccountContact>(`/accounts/${accountId}/contacts/${contactId}`, corpo);

export const definirSenhaDeContato = (accountId: string, contactId: string, password: string) =>
  api<void>(`/accounts/${accountId}/contacts/${contactId}/password`, {
    method: 'POST',
    body: { password },
  });

// ── chaves do TanStack Query ───────────────────────────────────────────────

/**
 * Uma raiz só, e invalidação pela raiz depois de escrever.
 *
 * Mexer num contato muda a contagem de contatos da conta na lista, e mexer na
 * conta muda o que a ficha mostra: enumerar cada chave afetada seria uma lista
 * para esquecer de atualizar.
 */
export const chavesDeClientes = {
  raiz: ['clientes'] as const,
  lista: (filtro: FiltroDeClientes = {}) =>
    ['clientes', 'lista', filtro.q ?? '', filtro.status ?? ''] as const,
  ficha: (id: string) => ['clientes', 'ficha', id] as const,
  contatos: (id: string) => ['clientes', 'contatos', id] as const,
};
